import { theme } from '@kvdbil/components';
import React, { useEffect, useRef } from 'react';
import Select, { SelectInstance, StylesConfig } from 'react-select';
import { objectSearchFieldOptionStyle } from './helpers';
import { CSSObject } from 'styled-components';
import { useLocation } from 'react-router';
import { useLastKeyboardInteraction } from '../../hooks/useLastKeyboardInteraction';

interface Props extends React.ComponentProps<typeof Select> {
  searchFieldOpen?: boolean;
}

const SearchFieldCategorySelect = ({
  name = 'category',
  searchFieldOpen,
  onMenuClose,
  ...props
}: Props) => {
  const location = useLocation();
  const selectRef = useRef<SelectInstance>(null);
  const { focusByKeyboard, onFocus, onBlur } = useLastKeyboardInteraction();

  const styles: StylesConfig = {
    control: (styles, { isFocused, menuIsOpen }) => {
      return {
        ...styles,
        ...({
          border: 'none',
          borderRadius: 0,
          borderTopLeftRadius: 30,
          borderBottomLeftRadius: menuIsOpen ? 0 : 30,
          lineHeight: '1.5rem',
          cursor: 'pointer',
          boxShadow: 'none',
          height: '60px',
          background: 'inherit',
          paddingInline: '1rem',
          outline: isFocused && focusByKeyboard ? '1px auto blue' : 'none'
        } as CSSObject)
      };
    },
    container: styles => ({
      ...styles,
      ...({
        width: '100%',
        '& svg': {
          width: '1.5rem',
          height: '1.5rem',
          color: theme.colors.grayscaleToned.dark1
        }
      } as CSSObject)
    }),
    menu: styles => ({
      ...styles,
      ...({
        position: 'absolute',
        borderRadius: 0,
        marginTop: 0,
        paddingBottom: '1rem',
        borderBottomLeftRadius: '30px',
        borderBottomRightRadius: '30px',
        boxShadow:
          '0px 5px 5px rgba(0, 0, 0, 0.15), 0px 4px 5px rgba(0, 0, 0, 0.09), 0px 2px 4px rgba(0, 0, 0, 0.1)', // theme.elevations.elevation4, but the Y axis is greater and spread is less so that it doesn't overlap the select
        top: 'auto',
        zIndex: 10000
      } as CSSObject)
    }),
    menuList: styles => ({
      ...styles,
      ...({
        paddingBlock: 0,
        borderBottomLeftRadius: '10px',
        borderBottomRightRadius: '10px'
      } as CSSObject)
    }),
    option: (styles, { isSelected, isFocused }) => ({
      ...styles,
      ...({
        ...objectSearchFieldOptionStyle(isSelected, isFocused)
      } as CSSObject)
    })
  };

  useEffect(() => {
    if (onMenuClose && selectRef.current) {
      onMenuClose();
      selectRef.current.blur();
    }
  }, [location, onMenuClose]);

  return (
    <Select
      {...props}
      instanceId="search-field-category"
      onMenuClose={onMenuClose}
      ref={selectRef}
      name={name}
      isSearchable={false}
      styles={styles}
      components={{
        IndicatorSeparator: () => null
      }}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
};

export default SearchFieldCategorySelect;
